import { safeErrorDetails } from "../../utils/logDiagnostics";
// src/pages/LoginPage.jsx
import React, { useEffect, useRef, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { Fingerprint, ScanFace } from "lucide-react";
import { isLoggedIn } from "../../utils/auth";
import {
  getRedirectFromSearch,
  resolvePostLoginPath,
  withRedirect,
} from "../../utils/authRedirect";
import { AUTH_CHANGED_EVENT } from "../../context/CartContext";
import "../css/AuthPages.css";
import { Capacitor } from "@capacitor/core";
import { initPush, syncPushTokenAfterLogin } from "../../notifications/push";
import {
  checkBiometricAvailability,
  hasBiometricLoginCredentials,
  getBiometricLoginCredentials,
  promptBiometricUnlock,
  saveBiometricLoginCredentials,
  consumeJustLoggedOut,
} from "../../utils/biometricLogin";

import AuthSplitLayout from "../../components/auth/AuthSplitLayout";

const API_BASE = process.env.REACT_APP_API_BASE_URL;

function isEmail(v = "") {
  const s = String(v).trim();
  return !!s && /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(s);
}

function isCF(v = "") {
  const s = String(v).trim();
  return !!s && /^[A-Za-z0-9]{16}$/.test(s);
}

function getRoleFromToken(token) {
  try {
    if (!token) return "";
    const base64 = token.split(".")[1];
    const payload = JSON.parse(
      atob(base64.replace(/-/g, "+").replace(/_/g, "/"))
    );
    return String(payload?.role || "").toLowerCase();
  } catch {
    return "";
  }
}

function getDefaultPostLoginPath(role) {
  return role === "fornitore" ? "/fornitore/ordini" : "/dashboard";
}

// Pagina richiesta prima del login (?redirect= / copia locale) se valida e
// ammessa per il ruolo, altrimenti la home di sempre. Consuma la copia locale.
function getPostLoginPath(token, search) {
  const role = getRoleFromToken(token);
  return resolvePostLoginPath({
    role,
    search,
    fallback: getDefaultPostLoginPath(role),
  });
}

export default function LoginPage({
  title = "Bentornato. Il resto lo pensiamo noi.",
  subtitle = "Accedi al tuo account per gestire ritiri, consegne e ordini in pochi click.",
  cardTitle = "Accedi al tuo account",
  cardSubtitle = "Inserisci le tue credenziali per continuare.",
  hideLinks = false,
} = {}) {
  const [identifier, setIdentifier] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [showPwd, setShowPwd] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  // conservato nei link verso registrazione / password dimenticata
  const redirectParam = getRedirectFromSearch(location.search);

  const loggedIn = isLoggedIn();

  // true quando finalizeLogin ha gia' deciso la destinazione: l'effect
  // "gia' loggato" qui sotto non deve navigare una seconda volta (la copia
  // locale e' gia' stata consumata e porterebbe alla home di sempre).
  const postLoginNavigating = useRef(false);

  const [bioReady, setBioReady] = useState(false);
  const [bioLabel, setBioLabel] = useState("Biometria");
  const [bioLoading, setBioLoading] = useState(false);

  // Anti-loop: il tentativo automatico deve avvenire al più una volta per
  // montaggio del componente. È un ref (non state) perché la scrittura deve
  // essere sincrona e non deve, di per sé, causare un re-render/re-run.
  const autoBiometricAttempted = useRef(false);

  // Segnale "arrivo qui subito dopo un logout esplicito": se presente,
  // sospende SOLO l'auto-trigger biometrico qui sotto (il bottone FaceID/
  // impronta manuale resta comunque disponibile per chi vuole rientrare
  // con lo stesso account). Va letto una sola volta al mount, prima che
  // l'effect di auto-trigger possa scattare (bioReady arriva async, quindi
  // c'è sempre tempo) — consumeJustLoggedOut() lo consuma alla lettura, per
  // cui vale solo per questo arrivo su LoginPage: alla prossima apertura
  // normale dell'app (sessionStorage azzerata) l'auto-login torna attivo.
  const skipAutoBiometricRef = useRef(false);
  useEffect(() => {
    skipAutoBiometricRef.current = consumeJustLoggedOut();
  }, []);

  // Snapshot sempre aggiornato di "l'utente sta già scrivendo nel form?",
  // letto (non "ascoltato") dall'effect di auto-trigger: evita di rieseguire
  // quell'effect ad ogni tasto, ma gli permette comunque di vedere lo stato
  // più recente nel raro caso in cui bioReady diventi true mentre l'utente
  // ha già iniziato a digitare.
  const formTouchedRef = useRef(false);
  useEffect(() => {
    formTouchedRef.current = !!(identifier || password);
  });

  useEffect(() => {
    if (!loggedIn) return;
    if (postLoginNavigating.current) return;

    const token = localStorage.getItem("token");
    navigate(getPostLoginPath(token, location.search), { replace: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loggedIn, navigate]);

  useEffect(() => {
    let alive = true;

    (async () => {
      if (!Capacitor.isNativePlatform()) return;

      try {
        const [bioInfo, hasCreds] = await Promise.all([
          checkBiometricAvailability(),
          hasBiometricLoginCredentials(),
        ]);

        if (!alive) return;

        setBioLabel(bioInfo?.biometryLabel || "Biometria");
        setBioReady(!!bioInfo?.available && !!hasCreds);
      } catch {
        if (!alive) return;
        setBioReady(false);
      }
    })();

    return () => {
      alive = false;
    };
  }, []);

  // Auto-trigger: se non sono loggato e la biometria è pronta (device
  // capace + credenziali già salvate), parte da sola una volta sola. Se
  // l'utente annulla o il FaceID fallisce, handleBiometricLogin gestisce già
  // l'errore/il fallback (vedi sotto) e qui non si ritenta da soli: il ref
  // resta true per tutta la vita del componente.
  useEffect(() => {
    if (loggedIn) return;
    if (!bioReady) return;
    if (autoBiometricAttempted.current) return;
    if (formTouchedRef.current) return;
    if (skipAutoBiometricRef.current) return;

    autoBiometricAttempted.current = true;
    handleBiometricLogin();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [bioReady, loggedIn]);

  const trimmed = identifier.trim();
  const idType = isCF(trimmed) ? "cf" : isEmail(trimmed) ? "email" : "unknown";

  const BioIcon = bioLabel.includes("Face") ? ScanFace : Fingerprint;

  const finalizeLogin = async (token, normalizedIdentifier = "", rawPassword = "") => {
    // Destinazione decisa PRIMA di salvare il token e degli await (push,
    // biometria): cosi' la copia locale viene consumata una volta sola.
    postLoginNavigating.current = true;
    const target = getPostLoginPath(token, location.search);

    localStorage.setItem("token", token);
    window.dispatchEvent(new CustomEvent(AUTH_CHANGED_EVENT,{detail:{reason:'login'}}));

    if (Capacitor.isNativePlatform()) {
      try {
        await initPush();
        await syncPushTokenAfterLogin();
      } catch (err) {
        console.error("Errore sync push post-login:", safeErrorDetails(err));
      }

      if (normalizedIdentifier && rawPassword) {
        try {
          await saveBiometricLoginCredentials({
            identifier: normalizedIdentifier,
            password: rawPassword,
          });
          setBioReady(true);
        } catch (err) {
          console.error("Errore salvataggio credenziali biometriche:", safeErrorDetails(err));
        }
      }
    }

    navigate(target, { replace: true });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (loading) return;

    setError("");

    const raw = identifier.trim();
    const looksLikeCF = /^[A-Za-z0-9]{16}$/.test(raw);
    const normalizedIdentifier = looksLikeCF ? raw.toUpperCase() : raw;

    if (!normalizedIdentifier || !password) {
      setError("Inserisci credenziali valide");
      return;
    }

    try {
      setLoading(true);

      const res = await fetch(`${API_BASE}/api/auth/login`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          identifier: normalizedIdentifier,
          password,
        }),
      });

      const data = await res.json().catch(() => ({}));

      if (!res.ok) {
        setError(data.error || "Credenziali non valide");
        return;
      }

      await finalizeLogin(data.token, normalizedIdentifier, password);

    } catch {
      setError("Errore di rete");
    } finally {
      setLoading(false);
    }
  };

  const handleBiometricLogin = async () => {
    if (bioLoading || loading) return;

    try {
      setError("");
      setBioLoading(true);

      const creds = await getBiometricLoginCredentials();
      if (!creds?.identifier || !creds?.password) {
        setError("Credenziali biometriche non disponibili");
        setBioReady(false);
        return;
      }

      await promptBiometricUnlock();

      const res = await fetch(`${API_BASE}/api/auth/login`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          identifier: creds.identifier,
          password: creds.password,
        }),
      });

      const data = await res.json().catch(() => ({}));

      if (!res.ok || !data?.token) {
        setError(data.error || "Accesso biometrico non riuscito");
        return;
      }

      await finalizeLogin(data.token, creds.identifier, creds.password);
    } catch (err) {
      if (err?.code === "userCancel") return;
      setError(err?.message || "Accesso biometrico non riuscito");
    } finally {
      setBioLoading(false);
    }
  };

  return (
    <AuthSplitLayout
      heroTitle={title}
      heroSubtitle={subtitle}
      cardTitle={cardTitle}
      cardSubtitle={cardSubtitle}
      hideCardSubtitleOnMobile
      hideHeroTextOnMobile
    >
      <form onSubmit={handleSubmit} noValidate>
        {bioReady && (
          <div className="bio-quickaccess">
            {bioLoading ? (
              <div className="bio-quickaccess__status">
                <BioIcon
                  size={28}
                  className="bio-quickaccess__status-icon"
                  aria-hidden="true"
                />
                <span>Verifica {bioLabel} in corso…</span>
              </div>
            ) : (
              <>
                <button
                  type="button"
                  className="bio-quickaccess__icon-btn"
                  onClick={handleBiometricLogin}
                  disabled={loading}
                  aria-label={`Accedi con ${bioLabel}`}
                >
                  <BioIcon size={26} aria-hidden="true" />
                </button>
                <span className="bio-quickaccess__caption">
                  Accedi con {bioLabel}
                </span>
              </>
            )}
          </div>
        )}

        {bioReady && (
          <div className="bio-divider">
            <span>oppure con email</span>
          </div>
        )}

        {error && (
          <div className="auth-error" role="alert">
            {error}
          </div>
        )}

        <div className="field">
          <label htmlFor="identifier">Email</label>
          <div className="id-input-wrap">
            <input
              id="identifier"
              type="text"
              value={identifier}
              onChange={(e) => setIdentifier(e.target.value)}
              required
              autoComplete="username"
              // Keep the keyboard stable while Android's IME is composing text.
              inputMode="email"
              autoCapitalize="none"
              autoCorrect="off"
              spellCheck={false}
              placeholder="Email o Codice Fiscale"
            />
            {idType !== "unknown" && (
              <span
                className={
                  "id-chip " +
                  (idType === "cf" ? "id-chip--cf" : "id-chip--email")
                }
              >
                {idType === "cf" ? "CF" : "Email"}
              </span>
            )}
          </div>
        </div>

        <div className="field">
          <label htmlFor="password">Password</label>
          <div className="pwd-wrap">
            <input
              id="password"
              type={showPwd ? "text" : "password"}
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
              autoComplete="current-password"
              placeholder="Password"
            />
            <button
              type="button"
              className="pwd-toggle"
              onClick={() => setShowPwd((v) => !v)}
              aria-label={showPwd ? "Nascondi password" : "Mostra password"}
            >
              {showPwd ? "🙈" : "👁️"}
            </button>
          </div>
        </div>

        <label className="remember-row">
          <input type="checkbox" />
          <span>Ricordami</span>
        </label>

        <div className="reg-actions">
          <button
            type="submit"
            className="reg-submit-btn"
            disabled={loading}
            aria-busy={loading}
          >
            {loading ? "Accesso…" : "Accedi"}
          </button>
        </div>

        {!hideLinks && (
          <div className="reg-bottom-links">
            <p>
              <Link className="auth-link" to={withRedirect("/forgot", redirectParam)}>
                Hai dimenticato la password?
              </Link>
            </p>
            <p className="muted">
              Non hai un account?{" "}
              <Link className="auth-link" to={withRedirect("/register", redirectParam)}>
                Registrati gratuitamente!
              </Link>
            </p>
          </div>
        )}
      </form>
    </AuthSplitLayout>
  );
}
